import { apiSlice } from '../../../core/api/apiSlice';

export interface CallSession {
  id: string;
  userId: string;
  platform: string | null;
  meetingTitle: string | null;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  finalSentimentScore: number | null;
  finalEngagementScore: number | null;
  finalBuyingIntentLabel: string | null;
  finalBuyingIntentScore: number | null;
  battlecardsGenerated: number;
  battlecardsUsed: number;
  objectionsDetected: number;
  nudgesShown: number;
  transcriptWordCount: number;
  topicsCovered: string[];
}

export interface CallSessionDetail extends CallSession {
  meetingUrl: string | null;
  transcriptSegments: { sequenceNo: number; speaker: string; isRep: boolean; text: string; confidence: number | null; recordedAt: string | null }[];
  sentimentSnapshots: { score: number; label: string | null; recordedAt: string }[];
  engagementSnapshots: { score: number; label: string | null; recordedAt: string }[];
  buyingIntentSnapshots: { label: string; confidence: number; trend: string | null; reasoning: string | null; signals: string[] | null; recordedAt: string }[];
  battlecardEvents: { cardId: string | null; trigger: string | null; source: string | null; salesStage: string | null; prospectQuote: string | null; branches: any[] | null; citations: any[] | null; selectedBranchId: string | null; wasUsed: boolean; generatedAt: string | null; selectedAt: string | null }[];
  objectionEvents: { primaryCategory: string; primaryConfidence: number | null; secondaryCategory: string | null; reasoning: string | null; detectedAt: string | null }[];
  nudgeEvents: { nudgeId: string | null; topicId: string | null; message: string | null; priority: string | null; outcome: string | null; shownAt: string | null; dismissedAt: string | null }[];
  chatEvents: { query: string; response: string | null; sources: any[] | null; context: any | null; createdAt: string }[];
}

export interface SessionDetailResponse {
  success: boolean;
  message: string;
  data: CallSessionDetail;
}

export interface SessionsListResponse {
  success: boolean;
  message: string;
  data: {
    data: CallSession[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface AnalyticsSummary {
  total_sessions: number;
  total_duration_seconds: number;
  avg_sentiment_score: number | null;
  avg_engagement_score: number | null;
  battlecard_usage_rate: number | null;
  top_objections: { category: string; count: number }[];
  buying_intent_distribution: Record<string, number>;
  calls_by_rep: { name: string; call_count: number; avg_sentiment: number | null }[];
  daily_calls: { date: string; calls: number; sentiment: number; conversion: number }[];
  period_days: number;
}

export interface AnalyticsSummaryResponse {
  success: boolean;
  message: string;
  data: AnalyticsSummary;
}

export const sessionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listSessions: builder.query<SessionsListResponse, { page?: number; limit?: number; user_id?: string; days?: number; search?: string } | void>({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.page) params.append('page', arg.page.toString());
        if (arg?.limit) params.append('limit', arg.limit.toString());
        if (arg?.user_id) params.append('user_id', arg.user_id);
        if (arg?.days) params.append('days', arg.days.toString());
        if (arg?.search) params.append('search', arg.search);
        const q = params.toString();
        return `/sessions${q ? `?${q}` : ''}`;
      },
      providesTags: ['Sessions'],
    }),

    getSessionDetail: builder.query<SessionDetailResponse, string>({
      query: (sessionId) => `/sessions/${sessionId}`,
      providesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),
  }),
});

export const {
  useListSessionsQuery,
  useLazyListSessionsQuery,
  useGetSessionDetailQuery,
} = sessionsApi;
